import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Validators}                          from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {map}                                            from 'rxjs/operators';
import {
  ApDynformsComponent
}                                                                         from '../../ap-dynforms/ap-dynforms.component';
import {
  ApDynformsConfigFieldset
}                                                                         from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                                         from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsValidator
}                                                                         from '../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigTextbox,
  ApDynformsConfigTextboxType
}                                                                         from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {DriverStore}                                                      from '../../stores/docu/driver.store';
import {InvitationStore}            from '../../stores/login/invitation.store';
import {FormStore, IEntryComponent} from '../../stores/layout/form.store';
import {FarmUsersStore}             from '../../stores/farm/farm.users.store';
import {nameOf}                                                           from '../../ap-core/utils/ap-name-of';
import {
  ApTranslationService
}                                                                         from '../../ap-utils/service/ap-translation.service';
import {UserGroupStore}                                                   from '../../stores/settings/user.group.store';
import {
  UserGroupsService
}                                                                         from '../../services/data/user-groups.service';
import {UserGroup}                                                        from '../../types/user-groups.type';
import {ApUsersInvitationEditComponentData, IUsersInvitationEditFormData} from './types/ap-users-invitation-edit.types';
import IDriver = Data.DocuContext.Driver.IDriver;
import IInvitations = Data.Authentication.IInvitations;

@Component({
  selector: 'ap-users-invitation-edit',
  template: `
    <ap-dynforms [fieldsets]="formConfig" [loading$]="loading$" [caption]="'Global_User_Create'">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_invitation_edit_cancel"
                type="button"
                kendoButton class="ap-form-button-left"
                (apSingleClick)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_invitation_edit_save"
                type="button" [disabled]="!dynForm.form.valid"
                kendoButton [themeColor]="'primary'" class="ap-form-button-right"
                (apSingleClick)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>
  `,
  standalone: false
})
export class ApUsersInvitationEditComponent implements OnInit, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('Global_User_Create');
  @ViewChild(ApDynformsComponent, {static: true})
  public dynForm: ApDynformsComponent;

  public formConfig: ApDynformsConfigFieldset[] = [];
  public loading$: Observable<boolean> = of(true);
  @Input() public data: ApUsersInvitationEditComponentData;

  private _formData: IUsersInvitationEditFormData;
  private _isNewMode = false;

  constructor(private formStore: FormStore,
              private driverStore: DriverStore,
              private farmUsersStore: FarmUsersStore,
              private userGroupsStore: UserGroupStore,
              private invitationStore: InvitationStore,
              private userGroupsService: UserGroupsService,
              private translationService: ApTranslationService) {
  }

  public ngOnInit(): void {
    this.setLoadingItems();
    this.setFormData();
    this.setFormFields();
  }

  private setFormData(): void {
    this._isNewMode = !this.data?.FarmUser;
    if (this._isNewMode) {
      const administratorGroupName = this.translationService.translate('Permission_Group_Administrator');
      const availableUserGroups = this.userGroupsService.getAvailableUserGroups(this.userGroupsStore.UserGroups);
      const administratorUserGroup = availableUserGroups.find(x => x.Name === administratorGroupName);
      this._formData = {
        Id: undefined,
        UserGroupId: administratorUserGroup?.Id,
        IsDriver: false,
        Email: '',
        DriverId: undefined
      };
    } else {
      this._formData = {
        Id: this.data.FarmUser.Id,
        UserGroupId: this.data.FarmUser.UsergroupUser[0].Usergroup_Id,
        Email: this.data.FarmUser.User.EMail,
        IsDriver: this.data.FarmUser.IsDriver,
        DriverId: this.data.FarmUser.Driver?.Id
      };
    }
  }

  public onSubmitClick(): void {
    const formData = this.dynForm.form.value as IUsersInvitationEditFormData;
    const driver = this.driverStore.getDriverById(formData.DriverId);
    const isDriver = !!driver?.Id;
    const inv = {
      Email: formData.Email ?? this._formData.Email,
      Usergroupid: formData.UserGroupId ?? this._formData.UserGroupId,
      Isdriver: isDriver,
      DriverId: driver?.Id
    } as IInvitations;
    if (this._isNewMode) {
      this.invitationStore.create(inv);
    } else {
      this.invitationStore.update(inv);
    }
    this.formStore.closeForm();
  }

  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  private setLoadingItems(): void {
    this.loading$ = combineLatest([
      this.driverStore.Loading$,
      this.farmUsersStore.Loading$,
      this.userGroupsStore.Loading$,
      this.invitationStore.Loading$
    ]).pipe(
      map(loadings => loadings.some(x => x))
    );
  }

  private setFormFields(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'Global__General_Information',
        legend: 'Global__General_Information',
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IUsersInvitationEditFormData>('UserGroupId'),
            label: 'Global_User_Group',
            valueField: nameOf<UserGroup>('Id'),
            textField: nameOf<UserGroup>('Name'),
            value: this._formData.UserGroupId,
            disabled: true,
            options: combineLatest([
              this.userGroupsStore.UserGroups$,
              this.invitationStore.Invitations$
            ]).pipe(
              map(([userGroups, invitations]) => {
                const availableUserGroups = this.userGroupsService.getAvailableUserGroups(userGroups);
                const filteredInvitations = invitations.filter(x => x.Usergroupid === this._formData.UserGroupId && x.Email === this._formData.Email);
                return availableUserGroups.filter(x => !filteredInvitations.some(y => y.Usergroupid === x.Id));
              })
            ),
            valuePrimitive: true
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<IUsersInvitationEditFormData>('Email'),
            label: 'Global__Email',
            type: ApDynformsConfigTextboxType.Email,
            value: this._formData.Email,
            disabled: !this._isNewMode,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required',
                always: true
              }),
              new ApDynformsValidator({
                validator: Validators.email,
                errorKey: 'Admin_Pages_Login_Reset_Password_Email_Error'
              }),
              new ApDynformsValidator({
                validator: Validators.nullValidator,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              }),
              new ApDynformsValidator({
                validator: Validators.minLength(1),
                errorKey: 'Settings__Msg_Vali_Value_Required'
              }),
              new ApDynformsValidator({
                validator: Validators.pattern('^[-_.\\w]+@([0-9a-zA-Z][-\\w]*[0-9a-zA-Z]\\.){1,300}[a-zA-Z]{2,9}$'),
                errorKey: 'Admin_Pages_Login_Reset_Password_Email_Error'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IUsersInvitationEditFormData>('DriverId'),
            label: 'Global__Worker',
            valueField: nameOf<IDriver>('Id'),
            textField: nameOf<IDriver>('FirstNameFullName'),
            value: this._formData.DriverId,
            options: combineLatest([
              this.driverStore.Drivers$,
              this.farmUsersStore.FarmUsers$
            ]).pipe(
              map(([drivers, farmUsers]) => {
                return drivers.filter(d => !farmUsers.some(fu => fu.Driver?.Id === d.Id));
              })
            ),
            valuePrimitive: true
          })
        ]
      })
    ];
  }
}
